<template>
  <main class="content">
    <Hero :title="data?.header.title"/>
    <div class="block maxw--body">
      <FormTextSearchField label="Zoeken" :animateLabel="false" v-model="search" class="mx-0"/>
      <CategoryFilter v-model="selectedCategories" :options="categories || []" v-model:private="privateOnly" :show-private-filter="user"/>
      <div class="text--center" v-if="items.length === 0 && !isLoading">
        <span class="subinfo">Geen artikelen gevonden</span>
      </div>
      <div class="grid--two mb-2" ref="holder" v-else>
        <BlogItem v-for="item in items" :key="item.id" :data="item"/>
      </div>
      <div class="block block--column" ref="loadMoreBlock">
        <div class="loader" v-if="isLoading">
          <Icon name="svg-spinners:tadpole" class="loader__icon"/>
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type BlogArticle from "@/typings/strapi/objects/blogArticle";
import type BlogHolderPage from "@/typings/strapi/pages/blogHolderPage";
import type { Strapi4RequestParams } from "@nuxtjs/strapi/dist/runtime/types";
import type Category from "~/typings/strapi/objects/category";

useHead({
  title: 'Blog'
});

const search = ref<string>('');
const debouncedSearch = refDebounced(search, 300);
const selectedCategories = ref<Array<Category>>([]);
const privateOnly = ref<boolean>(false);

const { find, findOne } = useStrapi();
const router = useRouter();
const route = useRoute();

const user = useStrapiUser();

const { data: categories } = await useAsyncData(
  'categories',
  () => find<Category>('categories'),
  {
    transform: flattenCollection
  }
);

const { data } = await useAsyncData(
  'blog-holder-page',
  () => findOne<BlogHolderPage>('blog-holder-page', { populate: ['header'], sort: ['publishedAt:desc'] }),
  {
    transform: flattenObject
  }
);

const itemsPage = ref<number>(1);
const totalPages = ref<number>(1);
const items = ref<Array<BlogArticle>>([]);
const { isLoading, startLoading, stopLoading } = useLoading();

onMounted(() => {
  const slugs = (route.query.category as string[]) || [];

  if (slugs.length > 0) {
    const categoriesFromUrl = categories.value?.filter(c => slugs.includes(c.slug));

    if (categoriesFromUrl && categoriesFromUrl.length > 0) {
      selectedCategories.value = categoriesFromUrl;
    }
  }

  fetchItems(1, search.value, selectedCategories.value);
})

const fetchItems = async (page: number, search: string = '', selectedCategories: Array<Category> = [], privateOnly: boolean = false, reset: boolean = false) => {
  startLoading();

  // Setup base configuration
  const config = {
    sort: 'createdAt:desc',
    pagination: {
      pageSize: 4,
      page: page
    },
    populate: [
      'thumbnail', 
      'attachments', 
      'categories'
    ]
  } as Strapi4RequestParams;

  // Create a list of filters to apply
  const filtersToApply = [];

  // If search is not empty, add a filter for the title
  if (search.length > 0) {
    filtersToApply.push({
      title: {
        $containsi: search
      }
    });
  }

  // If selected categories, add a filter for the categories
  if (selectedCategories.length > 0) {
    filtersToApply.push({
      categories: {
        id: {
          $in: selectedCategories.map(c => c.id)
        }
      }
    });
  }

  // If private only, add a filter for the private field
  if (privateOnly) {
    filtersToApply.push({
      private: {
        $eq: true
      }
    });
  }

  // Add filters to the configuration
  if (filtersToApply.length === 1) {
    // If filters is only one, just use that filter as key
    config.filters = filtersToApply[0];
  } else if (filtersToApply.length > 1) {
    // If multiple filters, create a combined query
    config.filters = {
      $and: filtersToApply
    };
  }

  const response = await find<BlogArticle>('blog-articles', config);

  totalPages.value = response.meta.pagination.pageCount;

  if (reset) {
    items.value = [];
  }

  const data = flattenCollection<BlogArticle>(response);

  data.forEach((blogItem) => {
    items.value?.push(blogItem);
  });

  itemsPage.value = page;
  stopLoading();
}

const holder = ref<HTMLDivElement>(); 
const loadMoreBlock = ref<HTMLDivElement>(); 

// TODO: look into when this stays true because the loadMoreBlock was still visible
// even after adding the new data
// Maybe hide or have a delay flag that it should check again after a delay if it's still visible
const loadMore = useElementVisibility(loadMoreBlock);

// If watch updates or loadMore is triggered, fetch new items
watch([loadMore, debouncedSearch], ([loadMore, search], [_, oldSearch]) => {
  if (loadMore && itemsPage.value < totalPages.value) {
    // Fetch items by loading more
    fetchItems(itemsPage.value + 1, search, selectedCategories.value, privateOnly.value);
  } else if (search !== oldSearch) {
    // Fetch items by new search, reset list
    fetchItems(1, search, selectedCategories.value, true);
  }
}, { deep: true });

// If selected categories change, update the URL and fetch new items
watch(selectedCategories, (selectedCategories) => {
  // Update the URL
  router.push({
    query: {
      category: selectedCategories.map(c => c.slug)
    }
  });

  fetchItems(1, search.value, selectedCategories, privateOnly.value, true);
}, { deep: true });

watch(privateOnly, (privateOnly) => {
  fetchItems(1, search.value, selectedCategories.value, privateOnly, true);
});

definePageMeta({
  pageTransition: pageTransitionConfig,
});
</script>
